/*
 * Saving a file the browser has already downloaded.
 *
 * Every download in the panel was doing the same four things by hand, and each
 * copy forgot a different one: the object URL was never revoked (the file stays
 * in memory for the life of the tab), the anchor was left in the document, the
 * filename was hard-coded rather than taken from the response, and the blob was
 * handed straight to the browser even when it was an error page, which is how a
 * failed download ended up "succeeding" with a 0-byte .xlsx on disk.
 */

//"attachment; filename=student_template.xlsx" and the RFC 5987 form
function filenameFromHeaders(headers) {
  const disposition =
    headers &&
    (headers["content-disposition"] || headers["Content-Disposition"]);
  if (!disposition) return null;

  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch (e) {
      //fall through to the plain form
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1].trim() : null;
}

/**
 * True when the server answered with JSON or HTML rather than the file.
 *
 * A 200 is not proof of success: PHP can emit a warning or a login page ahead of
 * the body, and axios will happily wrap that in a Blob.
 */
export function looksLikeFile(response) {
  const type = (response.data && response.data.type) || "";
  return !/^(application\/json|text\/html)/i.test(type);
}

/** Reads a Blob that turned out to hold an error message. */
export function messageFromBlob(blob) {
  return blob
    .text()
    .then((text) => {
      try {
        const parsed = JSON.parse(text);
        return parsed.message || parsed.error || null;
      } catch (e) {
        return text && text.trim() && !text.trim().startsWith("<")
          ? text.trim()
          : null;
      }
    })
    .catch(() => null);
}

/**
 * Save an axios response fetched with responseType: "blob".
 *
 * Resolves once the file is handed to the browser, and rejects with a readable
 * message when the response was not a file after all.
 *
 * @param {object} response      the axios response
 * @param {string} fallbackName  used when the server sent no filename
 */
export function saveBlobResponse(response, fallbackName) {
  if (!looksLikeFile(response)) {
    return messageFromBlob(response.data).then((message) => {
      throw new Error(message || "The server did not return a file.");
    });
  }

  const name = filenameFromHeaders(response.headers) || fallbackName;
  return Promise.resolve(saveBlob(response.data, name));
}

/** Save any Blob under a name, cleaning up after itself. */
export function saveBlob(blob, name) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", name);
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();

  //Safari needs the anchor to survive the click, and every browser leaks the
  //blob until the URL is revoked
  window.setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 0);
}

//one CSV field: quotes doubled, and anything with a separator wrapped
function csvField(value) {
  if (value === null || value === undefined) return "";

  const text = String(value);
  if (/[",\n\r]/.test(text)) {
    return '"' + text.replace(/"/g, '""') + '"';
  }
  return text;
}

/**
 * Export rows to a CSV file.
 *
 * @param {Array}  rows     the objects to write
 * @param {Array}  columns  [{ text, value }] - `value` may be a dotted path or a function
 * @param {string} name     file name, without the extension
 */
export function exportRowsToCsv(rows, columns, name) {
  const header = columns.map((column) => csvField(column.text)).join(",");

  const body = rows.map((row) =>
    columns
      .map((column) =>
        csvField(
          typeof column.value === "function"
            ? column.value(row)
            : readPath(row, column.value)
        )
      )
      .join(",")
  );

  //the BOM is what makes Excel read the file as UTF-8 rather than as the
  //system code page, which is why exported names came out mangled elsewhere
  const csv = "﻿" + [header].concat(body).join("\r\n");

  saveBlob(new Blob([csv], { type: "text/csv;charset=utf-8;" }), name + ".csv");
}

//"student_settings.morning_bus.license" -> the value, or "" at the first gap
function readPath(row, path) {
  return String(path)
    .split(".")
    .reduce((value, key) => (value === null || value === undefined ? null : value[key]), row);
}

export default saveBlobResponse;
